/**
 * API клієнт для doc.processing_receipts — Надходження з переробки (замовник).
 * Permission: production.processing.receipt.manage.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface ProcessingReceiptMaterialIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
}

export interface ProcessingReceiptMaterial extends ProcessingReceiptMaterialIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProcessingReceiptProductIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  account_code?: string | null;  // Счет учета продукції (деф.26)
  /** ч.679: «Планова сума» — база розподілу собівартості при кількох продуктах. */
  planned_amount?: string | null;
}

/** ч.679: рядок вкладки «Послуги» (послуги переробника з ПДВ). */
export interface ProcessingReceiptServiceIn {
  line_number: number;
  nomenclature_id?: string | null;
  description?: string | null;
  quantity?: string;
  price?: string | null;
  amount?: string;
  /** ч.685: «Ставка ПДВ» рядка (20/14/7/0); vat_amount рахується від неї. */
  vat_rate_percent?: string | null;
  vat_amount?: string;
}

export interface ProcessingReceiptService extends ProcessingReceiptServiceIn {
  id: string;
}

/** ч.679: рядок «Повернені матеріали» (повернення з 206 на склад). */
export interface ProcessingReceiptReturnedMaterialIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  /** ч.685: склад повернення (NULL = склад шапки). */
  warehouse_id?: string | null;
}

export interface ProcessingReceiptReturnedMaterial extends ProcessingReceiptReturnedMaterialIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProcessingReceiptProduct extends ProcessingReceiptProductIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProcessingReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  processor_contractor_id: string;
  warehouse_id: string;
  total_cost: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface ProcessingReceipt extends ProcessingReceiptListItem {
  contract_id: string | null;
  processing_service_amount: string;
  processing_service_vat: string;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  department_id?: string | null;
  nomenclature_group_id?: string | null;
  materials: ProcessingReceiptMaterial[];
  products: ProcessingReceiptProduct[];
  services: ProcessingReceiptService[];
  returned_materials: ProcessingReceiptReturnedMaterial[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ProcessingReceiptCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  processor_contractor_id: string;
  contract_id?: string | null;
  warehouse_id: string;
  processing_service_amount?: string;
  processing_service_vat?: string;
  comment?: string | null;
  project_id?: string | null;
  department_id?: string | null;
  nomenclature_group_id?: string | null;
  materials?: ProcessingReceiptMaterialIn[];
  products?: ProcessingReceiptProductIn[];
  services?: ProcessingReceiptServiceIn[];
  returned_materials?: ProcessingReceiptReturnedMaterialIn[];
}

export type ProcessingReceiptUpdate = Partial<Omit<ProcessingReceiptCreate, 'organization_id'>>;

export const processingReceipts = {
  list: (params?: {
    organization_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc';
    processor_contractor_id?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<ProcessingReceiptListItem>>('/processing-receipts', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<ProcessingReceipt>(`/processing-receipts/${id}`).then((r) => r.data),
  create: (payload: ProcessingReceiptCreate) =>
    api.post<ProcessingReceipt>('/processing-receipts', payload).then((r) => r.data),
  update: (id: string, payload: ProcessingReceiptUpdate) =>
    api.patch<ProcessingReceipt>(`/processing-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/processing-receipts/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<ProcessingReceipt>(`/processing-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<ProcessingReceipt>(`/processing-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/processing-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
